/**
 * O aviso de que uma versão nova do app está pronta.
 *
 * ## Por que ele existe: o app se recarregava debaixo de quem o usava
 *
 * `vite-plugin-pwa` roda em `registerType: 'autoUpdate'`, e o registrador que
 * ele gera faz literalmente isto quando o service worker novo ativa:
 *
 * ```js
 * wb.addEventListener('activated', (event) => {
 *   if (event.isUpdate || event.isExternal) {
 *     if (onNeedReload) onNeedReload()
 *     else window.location.reload()
 *   }
 * })
 * ```
 *
 * Sem `onNeedReload`, é `window.location.reload()` — na hora, no meio do que a
 * pessoa estiver fazendo. Num dia de oito publicações isso aconteceu oito
 * vezes: toque num item, o router começa a navegar, o SW ativa, a página
 * recarrega no meio da transição e o boot inteiro recomeça (autenticação,
 * `bootstrapHousehold` a frio, leituras). Parece que travou, e a URL fica na do
 * meio do caminho.
 *
 * Passar `onNeedReload` **desliga** esse reload e devolve a decisão ao app.
 *
 * ## O que o app faz com ela
 *
 * Duas coisas, nesta ordem de preferência:
 *
 * 1. **Se a aba for escondida, recarrega ali.** Ninguém está olhando; quando
 *    voltar, já é a versão nova e nada piscou. É o caso comum — o celular vai
 *    para o bolso entre uma gôndola e outra.
 * 2. **Enquanto estiver à vista, só avisa.** Uma barra discreta com "Atualizar".
 *    Quem está no meio de registrar um preço termina primeiro.
 *
 * O reload em aba escondida pode ser adiado pelo navegador, e tudo bem: o pior
 * caso é a barra continuar lá, que é o comportamento (1) degradando para o (2).
 *
 * ## E o app JÁ está rodando com o SW novo
 *
 * `autoUpdate` mantém `skipWaiting` e `clientsClaim`, então quando isto é
 * chamado o service worker novo **já assumiu** — o que ficou velho é o
 * JavaScript em memória desta aba. Recarregar é o que os alinha; adiar o
 * recarregamento é adiar o alinhamento, não criar um desalinho.
 */

type Ouvinte = () => void

let pendente = false
const ouvintes = new Set<Ouvinte>()

// O ouvinte é registrado UMA vez, no carregamento do módulo, e não a cada
// aviso: dentro do `avisarNovaVersao` ele se acumularia a cada chamada, e o
// `document` sobrevive a tudo — inclusive entre casos de teste. Quem decide se
// há o que fazer é a guarda `pendente`.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden' && pendente) window.location.reload()
})

/** Chamado pelo `registerSW` quando a versão nova está pronta. */
export function avisarNovaVersao(): void {
  if (pendente) return
  pendente = true
  for (const ouvinte of ouvintes) ouvinte()
}

/** Há atualização esperando? Lido no primeiro render de quem assina. */
export function temNovaVersao(): boolean {
  return pendente
}

/** Avisa quando uma versão nova ficar pronta. Devolve o cancelador. */
export function assinarNovaVersao(ouvinte: Ouvinte): () => void {
  ouvintes.add(ouvinte)
  return () => {
    ouvintes.delete(ouvinte)
  }
}

/** Só para os testes: devolve o módulo ao estado de app recém-aberto. */
export function esquecerNovaVersao(): void {
  pendente = false
  ouvintes.clear()
}
